import { memo, useCallback, useMemo, useState } from "react";
import {
  AccountData,
  Event,
  ThemeType,
  VerificationState,
} from "@markit/common.types";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import VerificationCodeButton, {
  VerificationCodeOrigin,
} from "../Verification/VerificationCodeButton";
import CustomTextField from "../../components/CustomTextField";
import {
  PhoneErrorType,
  PhoneNumberValidType,
  censoredWordsDetected,
  detectedCensored,
  isExternalGenericLink,
} from "@markit/common.utils";
import { checkPhoneNumberValidCountryType } from "../../utils/FormatPhoneNumber";
import CustomPhoneTextField from "../TextFields/CustomPhoneTextField";
import { Icon } from "@iconify/react";
import { showPhoneInputError } from "../../utils/phoneUtils";
import CompliancePhoneOptIn from "../Compliance/CompliancePhoneOptIn";
import { isDesktop } from "react-device-detect";
import { useLocation } from "react-router-dom";
import { generateTextFromJSON } from "../../utils/stringUtils";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import ExpandableBox from "../Containers/ExpandableBox";
import { useOnMount } from "../../hooks/useOnMount";
import { useSelector } from "react-redux";
import { getAccountState, LoginState } from "../../redux/slices/accountSlice";
import ReadOnlyRichTextEditor from "../TextFields/ReadOnlyRichTextEditor";

type NameAndPhoneNumberInputProps = {
  host: AccountData;
  verificationState: VerificationState;
  setVerificationState: (verificationState: VerificationState) => void;
  optedOut: boolean;
  setOptedOut: (optedOut: boolean) => void;
  optedOutCreator: boolean;
  setOptedOutCreator: (optedOutCreator: boolean) => void;
  tempFullName: string;
  setTempFullName: (tempFullName: string) => void;
  tempPhoneNumber: string;
  setTempPhoneNumber: (tempPhoneNumber: string) => void;
  setUid: (uid: string) => void;
  processing: boolean;
  setIsOrganizer?: (isOrganizer: boolean) => void;
  event?: Event; // If not defined, it's profile subscribe flow
  callbackOnVerify?: () => void;
  themeType?: ThemeType;
};

export const NameAndPhoneNumberInput = memo(function NameAndPhoneNumberInputFn(
  props: NameAndPhoneNumberInputProps
) {
  const {
    host,
    verificationState,
    setVerificationState,
    optedOut,
    setOptedOut,
    optedOutCreator,
    setOptedOutCreator,
    tempFullName,
    setTempFullName,
    tempPhoneNumber,
    setTempPhoneNumber,
    setUid,
    setIsOrganizer,
    processing,
    event,
    callbackOnVerify,
    themeType,
  } = props;
  const { theme } = useTheme(themeType);
  const pathname = useLocation().pathname;
  const { loggedIn } = useSelector(getAccountState).account;

  const [showFullNameError, setShowFullNameError] = useState(false);
  const [showPhoneNumberError, setShowPhoneNumberError] =
    useState<PhoneErrorType>({ isError: false, message: "" });
  const [showCodeInputError, setShowCodeInputError] = useState(false);
  const [optInChecked, setOptInChecked] = useState(false);

  // Pre-set opt in as checked if user is logged in, verified, and the showTextComplianceCheckbox is true
  useOnMount(() => {
    if (
      loggedIn === LoginState.LOGGED_IN &&
      host.showTextComplianceCheckbox &&
      !event &&
      verificationState === VerificationState.VERIFIED
    ) {
      setOptInChecked(true);
    }
  });

  // Helps check if event body is empty or not
  const eventDescription = useMemo(
    () => event && generateTextFromJSON(event.body),
    [event]
  );

  // Checks if follower subscription flow is the membership flow
  const isMembershipFlow = useMemo(
    () => pathname.includes("memberships"),
    [pathname]
  );

  const showError = useCallback(() => {
    let error = false;

    if (showPhoneInputError(tempPhoneNumber, setShowPhoneNumberError)) {
      error = true;
    }

    if (tempFullName === "" || detectedCensored(tempFullName)) {
      if (detectedCensored(tempFullName)) {
        alert(
          `Full name contains banned words and must be removed (${censoredWordsDetected(
            tempFullName
          )})`
        );
      }
      setShowFullNameError(true);
      error = true;
    }

    // todo (jonathan): not sure this is needed. Seems like all it does is turn code input red when you resend code
    if (verificationState === VerificationState.VERIFYING) {
      setShowCodeInputError(true);
    }

    return error;
  }, [tempFullName, tempPhoneNumber, verificationState]);

  const renderEventRelatedHeader = useMemo(
    () =>
      event ? (
        isExternalGenericLink(event.eventType) ? (
          <div className="ColumnNormal">
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <span className="sectionTitle" style={theme.PrimaryText}>
                {event.title}
              </span>
              {eventDescription ? (
                <ReadOnlyRichTextEditor
                  body={event.body}
                  theme={theme}
                  className={`post-content-editor ${
                    isDesktop ? "" : "mobile-editor"
                  }`}
                />
              ) : null}
            </div>
            <HorizontalDivider altMargin={20} />
          </div>
        ) : (
          <div className="ColumnNormal" style={{ gap: 4, paddingBottom: 14 }}>
            <span
              className={event ? "NameAndNumber" : "sectionTitle"}
              style={{ ...theme.PrimaryText }}
            >
              Contact Info
            </span>
            {verificationState !== VerificationState.VERIFIED ? (
              <span style={{ color: Colors.GRAY2, fontSize: 12 }}>
                Just for updates.
              </span>
            ) : null}
          </div>
        )
      ) : null,
    [event, eventDescription, theme, verificationState]
  );

  return (
    <ExpandableBox className={"ColumnNormal"}>
      {event ? renderEventRelatedHeader : null}
      <div className="ColumnNormal" style={{ gap: "14px" }}>
        <CustomTextField
          value={tempFullName}
          placeholder="Your name"
          disabled={
            processing || verificationState === VerificationState.VERIFIED
          }
          inputMode="text"
          borderRadius={12}
          onChange={(name: any) => {
            setShowFullNameError(false);
            setTempFullName(name.target.value);
          }}
          error={showFullNameError}
          eventTheme={themeType}
          containerStyles={{ alignSelf: undefined, width: undefined }}
        />
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <CustomPhoneTextField
            placeholder="Phone number"
            phoneNumber={tempPhoneNumber}
            onPhoneChange={(value) => {
              setShowPhoneNumberError({ isError: false, message: "" });
              setTempPhoneNumber(value);
            }}
            showPhoneError={showPhoneNumberError}
            disabled={
              processing ||
              verificationState === VerificationState.VERIFIED ||
              verificationState === VerificationState.VERIFYING
            }
            endAdornment={
              verificationState === VerificationState.VERIFIED ? (
                <div
                  className="AlignedRow"
                  style={{ gap: "5px", color: Colors.GREEN2 }}
                >
                  <span className="bodyMedium">
                    {checkPhoneNumberValidCountryType(tempPhoneNumber) ===
                    PhoneNumberValidType.INTERNATIONAL
                      ? "Intl"
                      : "Verified"}
                  </span>
                  <Icon
                    icon="ion:checkmark-circle"
                    height={20}
                    style={{ color: Colors.GREEN2 }}
                  />
                </div>
              ) : verificationState === VerificationState.UNVERIFIED ? (
                showPhoneNumberError.isError ||
                (tempPhoneNumber &&
                  checkPhoneNumberValidCountryType(tempPhoneNumber) !==
                    PhoneNumberValidType.NONE) ? (
                  <Icon
                    icon={
                      showPhoneNumberError.isError
                        ? "ion:close-circle-outline"
                        : "ion:checkmark-circle-outline"
                    }
                    height={20}
                    style={{
                      marginBottom: -2,
                      color: showPhoneNumberError.isError
                        ? Colors.RED2
                        : Colors.GREEN2,
                    }}
                  />
                ) : undefined
              ) : undefined
            }
            autoCompleteOn
            themeType={themeType}
          />
          {host.showTextComplianceCheckbox && !event ? (
            <CompliancePhoneOptIn
              isChecked={optInChecked}
              setIsChecked={setOptInChecked}
              hostShowComplianceCheckbox={host.showTextComplianceCheckbox}
              hostPhoneType={host.customer.phoneType}
              hostName={host.fullName}
              disabled={verificationState !== VerificationState.UNVERIFIED}
            />
          ) : null}
        </div>
        {/** For when an acount is being converted to subaccount and phone number gets moved */}
        {/* {event?.createdBy === "Invc1dY9mXNQfMlqsNE289bR0HS2" ||
        host.fullName === "ROBERT MARTINEZ" ? (
          <p
            style={{
              fontSize: 12,
              fontWeight: "500",
              textAlign: "center",
              marginInline: 20,
              color: "#929292",
            }}
          >
            <span
              style={{ color: Colors.RED1, fontWeight: "600", fontSize: 14 }}
            >
              Attention!
            </span>{" "}
            There are some maintenance changes currently being done. Please
            check back in half an hour. Thank you.
          </p>
        ) : ( */}
        <VerificationCodeButton
          event={event}
          origin={
            event
              ? VerificationCodeOrigin.EVENT
              : VerificationCodeOrigin.PROFILE
          }
          buttonText={
            event
              ? "Verify Number"
              : isMembershipFlow
              ? "Continue"
              : "Subscribe"
          }
          tempFullName={tempFullName}
          tempPhoneNumber={tempPhoneNumber}
          setNewUid={setUid}
          verificationState={verificationState}
          setVerificationState={setVerificationState}
          optedOut={optedOut}
          setOptedOut={setOptedOut}
          optedOutCreator={optedOutCreator}
          setOptedOutCreator={setOptedOutCreator}
          showCodeInputError={showCodeInputError}
          setShowCodeInputError={setShowCodeInputError}
          showError={showError}
          host={host}
          callbackOnVerify={callbackOnVerify}
          processing={processing}
          disableVerifyNumber={
            !optInChecked && host.showTextComplianceCheckbox && !event
          }
          buttonContainerStyles={{
            backgroundColor: theme.ButtonBG.backgroundColor,
            color: theme.ButtonText.color,
          }}
          themeType={themeType}
        />
        {/* )} */}
      </div>
    </ExpandableBox>
  );
});
